import { TrendingUp, Heart, Clock, Star, Lightbulb } from "lucide-react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { useTranslation } from "react-i18next";

const OverviewSection = ({ report }) => {
   const { t } = useTranslation();
  // Defensive defaults
  const performanceData = report?.performance_ai || {};
  const behavioralData = report?.behavioural_ai || {};
  const emotionalData = report?.emotional_ai || {};
  const progressData = report?.progress_ai || {};

  const performanceTopics = performanceData.in_progress_topics?.length || 0;
  const studyChronotype = behavioralData.study_chronotype || "N/A";
  const studyTimes = behavioralData.optimal_study_times?.join(", ") || "N/A";
  const emotionalState = emotionalData.overall_emotional_state || "N/A";
  const interestSubjects = progressData.high_interest_subjects?.join(", ") || "N/A";
  const summaryInsight = performanceData.summary_insight ||  t("overview.noInsights");
  const overallFeedback = report?.overall_feedback || t("overview.noFeedback");
  const supportRecommendations = emotionalData.support_recommendations || [];
  const strengths = performanceData.strengths || [];
  const needsSupport = performanceData.needs_support_topics || [];

  // ---- Build hourly activity (0–23 with default 0) ----
  const allHours = Array.from({ length: 24 }, (_, i) => ({
    hour: `${String(i).padStart(2, "0")}:00`,
    activity: 0,
  }));
  const activityMap = behavioralData.data_for_graph?.hourly_activity || {};
  const hourlyActivity = allHours.map((h) => ({
    ...h,
    activity: activityMap[h.hour.split(":")[0]] || 0,
  }));

  return (
    <div className="space-y-8">
      {/* ---- Overview Cards ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-green-600 p-4 rounded-lg border-2 border-green-400 shadow-md">
          <div className="flex items-center space-x-2 mb-2">
            <TrendingUp className="w-6 h-6 text-white font-bold" />
            <h4 className="font-bold text-white">{t("overview.performance.title")}</h4>
          </div>
          <p className="text-sm font-semibold text-white">
            {performanceTopics}  {t("overview.performance.topicsInProgress")}
          </p>
          <p className="text-xs font-medium text-white mt-1">{summaryInsight}</p>
        </div>

        <div className="bg-orange-600 p-4 rounded-lg border-2 border-orange-400 shadow-md">
          <div className="flex items-center space-x-2 mb-2">
            <Clock className="w-6 h-6 text-white font-bold" />
            <h4 className="font-bold text-white">{t("overview.studyPattern.title")}</h4>
          </div>
          <p className="text-sm font-semibold text-white capitalize">
            {studyChronotype.replace("_", " ")}
          </p>
          <p className="text-xs font-medium text-white mt-1"> {t("overview.studyPattern.peak")}: {studyTimes}</p>
        </div>

        <div className="bg-red-600 p-4 rounded-lg border-2 border-red-400 shadow-md">
          <div className="flex items-center space-x-2 mb-2">
            <Heart className="w-6 h-6 text-white font-bold" />
            <h4 className="font-bold text-white">{t("overview.emotionalState.title")}</h4>
          </div>
          <p className="text-sm font-semibold text-white capitalize">{emotionalState}</p>
          <p className="text-xs font-medium text-white mt-1">
             {t("overview.emotionalState.confidence")}: {emotionalData.confidence_trend || "N/A"}
          </p>
        </div>

        <div className="bg-blue-600 p-4 rounded-lg border-2 border-blue-400 shadow-md">
          <div className="flex items-center space-x-2 mb-2">
            <Star className="w-6 h-6 text-white font-bold" />
            <h4 className="font-bold text-white">{t("overview.interests.title")}</h4>
          </div>
          <p className="text-sm font-semibold text-white">{interestSubjects}</p>
          <p className="text-xs font-medium text-white mt-1">{t("overview.interests.engagement")}</p>
        </div>
      </div>

      {/* ---- AI Insight ---- */}
      <div className="bg-slate-50 p-6 rounded-xl shadow border border-slate-200">
        <h3 className="font-bold text-slate-800 mb-2">{t("overview.aiInsight.title")}</h3>
        <p className="text-slate-700 text-sm leading-relaxed">{overallFeedback}</p>
      </div>

      {/* ---- Support Recommendations ---- */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h3 className="font-bold text-slate-800 mb-3 flex items-center">
          <Lightbulb className="w-5 h-5 text-yellow-500 mr-2" />
           {t("overview.support.title")}
        </h3>
        {supportRecommendations.length > 0 ? (
          <ul className="list-disc pl-6 space-y-1 text-sm text-slate-700">
            {supportRecommendations.map((rec, i) => (
              <li key={i}>{rec}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">{t("overview.support.none")}</p>
        )}
      </div>

      {/* ---- Strengths & Struggles ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-700 mb-2">{t("overview.strengths.title")}</h4>
          {strengths.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {strengths.map((s, i) => (
                <span key={i} className="bg-green-200 text-green-800 px-2 py-1 rounded text-xs">
                  {s}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">{t("overview.strengths.none")}</p>
          )}
        </div>

        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h4 className="font-semibold text-red-700 mb-2"> {t("overview.needsSupport.title")}</h4>
          {needsSupport.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {needsSupport.map((s, i) => (
                <span key={i} className="bg-red-200 text-red-800 px-2 py-1 rounded text-xs">
                  {s}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">{t("overview.needsSupport.none")}</p>
          )}
        </div>
      </div>

      {/* ---- Hourly Activity ---- */}
      <div className="bg-slate-50 p-6 rounded-xl shadow-sm border border-slate-200">
        <h3 className="font-bold text-slate-800 mb-3"> {t("overview.hourlyActivity.title")}</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={hourlyActivity}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Line type="monotone" dataKey="activity" stroke="#10b981" strokeWidth={2} dot />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OverviewSection;
